(() => {
  const ROOT_SELECTOR = '[data-sales-promotions-grid]';
  const RESULTS_SELECTOR = 'results-list.product-grid-container[section-id]';
  let activeRequest = null;

  function getRoot() {
    return document.querySelector(ROOT_SELECTOR);
  }

  function normalize(value) {
    return String(value ?? '').trim().toLocaleLowerCase();
  }

  function getFilterOptions(root) {
    return Array.from(root.querySelectorAll('[data-sales-filter-link]'));
  }

  function resolveFilterOption(root, requestedValue) {
    const options = getFilterOptions(root);

    if (!requestedValue) {
      return options.find((option) => !option.dataset.salesFilterValue) || null;
    }

    const requested = normalize(requestedValue);

    return (
      options.find((option) => {
        return (
          normalize(option.dataset.salesFilterValue) === requested ||
          normalize(option.dataset.salesFilterLabel) === requested
        );
      }) || null
    );
  }

  function getProductSection() {
    const resultsList = document.querySelector(RESULTS_SELECTOR);
    if (!resultsList) return null;

    const sectionId = resultsList.getAttribute('section-id');
    const wrapper = resultsList.closest('.shopify-section');

    if (!sectionId || !wrapper) return null;

    return { sectionId, wrapper };
  }

  async function fetchSection(sectionId, url, signal) {
    const requestURL = new URL(url.toString());
    requestURL.searchParams.delete('page');
    requestURL.searchParams.set('section_id', sectionId);

    const response = await fetch(requestURL.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal,
    });

    if (!response.ok) {
      throw new Error(`Section request failed (${sectionId}): ${response.status}`);
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const nextSection = doc.getElementById(`shopify-section-${sectionId}`);

    if (!nextSection) {
      throw new Error(`Section ${sectionId} was not found in the response.`);
    }

    return nextSection;
  }

  function setActivePill(root, activeOption) {
    getFilterOptions(root).forEach((option) => {
      const isActive = option === activeOption;

      option.classList.toggle('is-active', isActive);
      option.dataset.active = isActive ? 'true' : 'false';

      if (isActive) {
        option.setAttribute('aria-current', 'true');
      } else {
        option.removeAttribute('aria-current');
      }
    });
  }

  function optionForURL(root, url) {
    const targetPath = new URL(url, window.location.origin).pathname.replace(/\/+$/, '');

    return (
      getFilterOptions(root).find((option) => {
        const optionPath = new URL(option.href, window.location.origin).pathname.replace(/\/+$/, '');
        return optionPath === targetPath;
      }) || resolveFilterOption(root, '')
    );
  }

  async function renderProducts(root, url, { historyMode = 'push', scroll = false } = {}) {
    if (!root) return false;

    const productSection = getProductSection();
    if (!productSection) {
      console.error('[Sales promotions grid] Could not locate the BODi main collection results-list section.');
      return false;
    }

    if (activeRequest) activeRequest.abort();
    activeRequest = new AbortController();

    root.classList.add('is-loading');
    root.setAttribute('aria-busy', 'true');
    productSection.wrapper.classList.add('is-loading');
    productSection.wrapper.setAttribute('aria-busy', 'true');

    try {
      const nextProductSection = await fetchSection(
        productSection.sectionId,
        url,
        activeRequest.signal
      );

      const currentProductSection = getProductSection();

      if (!currentProductSection) {
        throw new Error('The current BODi collection section disappeared before it could be updated.');
      }

      currentProductSection.wrapper.replaceWith(nextProductSection);

      const cleanURL = new URL(url.toString());
      cleanURL.searchParams.delete('section_id');
      cleanURL.searchParams.delete('page');

      if (historyMode === 'push') {
        window.history.pushState({ salesPromotions: true }, '', cleanURL.toString());
      } else if (historyMode === 'replace') {
        window.history.replaceState({ salesPromotions: true }, '', cleanURL.toString());
      }

      const currentRoot = getRoot();
      if (currentRoot) {
        setActivePill(currentRoot, optionForURL(currentRoot, cleanURL));
      }

      document.dispatchEvent(
        new CustomEvent('bodi:sales-promotions-grid:updated', {
          detail: {
            url: cleanURL.toString(),
            sectionId: productSection.sectionId,
          },
        })
      );

      if (scroll) {
        requestAnimationFrame(() => {
          document
            .getElementById('SalesProductGrid')
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }

      return true;
    } catch (error) {
      if (error.name === 'AbortError') return false;

      console.error('[Sales promotions grid]', error);

      // Progressive-enhancement fallback: only do a normal navigation if
      // Shopify's section request genuinely fails.
      const fallbackURL = new URL(url.toString());
      fallbackURL.searchParams.delete('section_id');
      window.location.assign(fallbackURL.toString());
      return false;
    } finally {
      activeRequest = null;

      const currentRoot = getRoot();
      currentRoot?.classList.remove('is-loading');
      currentRoot?.removeAttribute('aria-busy');

      const currentProductSection = getProductSection();
      currentProductSection?.wrapper.classList.remove('is-loading');
      currentProductSection?.wrapper.removeAttribute('aria-busy');
    }
  }

  async function applyFilter(filterValue, { scroll = false } = {}) {
    const root = getRoot();
    if (!root) return false;

    const option = resolveFilterOption(root, filterValue);

    if (!option) {
      console.info(`[Sales promotions grid] No configured filter pill matches "${filterValue}".`);

      if (scroll) {
        document
          .getElementById('SalesProductGrid')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      return false;
    }

    if (option.dataset.active === 'true') {
      if (scroll) {
        document
          .getElementById('SalesProductGrid')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return true;
    }

    const url = new URL(option.href, window.location.origin);
    url.searchParams.delete('page');

    return renderProducts(root, url, { historyMode: 'push', scroll });
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-sales-filter-link]');
    if (!link) return;

    const root = link.closest(ROOT_SELECTOR);
    if (!root) return;

    event.preventDefault();

    applyFilter(link.dataset.salesFilterValue || '', { scroll: false });
  });

  window.addEventListener('popstate', () => {
    const root = getRoot();
    if (!root) return;

    renderProducts(root, new URL(window.location.href), {
      historyMode: 'none',
      scroll: false,
    });
  });

  window.BODiSalesPromotionsGrid = {
    applyFilter,
    refresh() {
      const root = getRoot();
      if (!root) return Promise.resolve(false);

      return renderProducts(root, new URL(window.location.href), {
        historyMode: 'none',
        scroll: false,
      });
    },
  };
})();
